import React, { FunctionComponent, useEffect, useState } from "react";
import Table, { ColumnsType } from "antd/lib/table";
import { Status, StatusEnum } from "../../../common/Status";
import moment from "moment";
import { useSelector } from "react-redux";
import { ICompany, selectCompanies, selectCompaniesAreFetching } from "../../../../features/company/companySlice";
import { TileTile } from "../common/TileTitle";
import { selectCompanyTypesAreFetching, selectCompanyTypesDict } from "../../../../features/companyType/companyTypeSlice";
import { Button, Tooltip } from "antd";

import styles from './index.module.scss';
import { getCoutriesDictionary } from "../common/utils";
import { CogIcon } from "../../../common/CogIcon";
import { DataType, exportToExcel, HeadersType } from "../../common/excelExport";
import { Dot } from "../../common/Dot";
import { getTranslations } from "../../../../features/translations/translationsUtils";

const EXCEL_HEADERS: () => HeadersType = () => [
  getTranslations('REPORT_COMPANY'),
  getTranslations('REPORT_COMPANY_STATUS'),
  getTranslations('REPORT_COMPANY_TYPE'),
  getTranslations('REPORT_COUNTRY'),
  getTranslations('REPORT_CITY'),
  getTranslations('REPORT_CONTRACT_START_DATE'),
  getTranslations('REPORT_NOTIFICATION_SMS'),
  getTranslations('REPORT_NOTIFICATION_PUSH') ,
  getTranslations('REPORT_NOTIFICATION_EMAIL'),
  getTranslations('REPORT_USERS_COUNT'),
  getTranslations('REPORT_ENABLED_USERS_COUNT'),
  getTranslations('REPORT_DISABLED_USERS_COUNT'),
];

interface ICompanyReport extends ICompany {
  countryName: string,
}

interface Props { }
export const CompaniesTable: FunctionComponent<Props> = () => {
  const isFetchingCompanies = useSelector(selectCompaniesAreFetching);
  const isFetchingCompanyTypes = useSelector(selectCompanyTypesAreFetching);
  const allCompanies = useSelector(selectCompanies);
  const companyTypes = useSelector(selectCompanyTypesDict);
  const [companiesData, setCompaniesData] = useState<ICompanyReport[]>([]);
  const dataIsFetching = isFetchingCompanies || isFetchingCompanyTypes;

  useEffect(() => {
    if (dataIsFetching || Object.keys(allCompanies).length === 0) {
      return;
    }

    const countryDataByCode = getCoutriesDictionary();
    const allCompaniesEnhanced: ICompanyReport[] = [];
    for (const companyId in allCompanies) {
      const companyBase = allCompanies[companyId];
      const companyTypeId = companyBase.companyType || '';
      const companyTypeName = (companyTypes[companyTypeId as string] || { name: '' }).name
      allCompaniesEnhanced.push({
        ...companyBase,
        countryName: countryDataByCode[companyBase.country].countryName || '',
        companyTypeName: companyTypeName,
      });
    }
    setCompaniesData(allCompaniesEnhanced);
  }, [dataIsFetching, allCompanies, companyTypes])

  const columns: ColumnsType<ICompanyReport> = [
    {
      width: 300,
      title: getTranslations('REPORT_COMPANY'),
      dataIndex: 'name',
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "ascend",
      ellipsis: true,
      sorter: (a: ICompanyReport, b: ICompanyReport) => a.name.localeCompare(b.name),
    },
    {
      width: 100,
      title: getTranslations('REPORT_COMPANY_STATUS'),
      dataIndex: 'status',
      render: (status, entry) => {
        return <Status status={status} />
      },
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => a.status.localeCompare(b.status)
    },
    {
      width: 150,
      title: getTranslations('REPORT_COMPANY_TYPE'),
      dataIndex: 'companyTypeName',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => (a.companyTypeName || '').localeCompare(b.companyTypeName || ''),
    },
    {
      width: 150,
      title: getTranslations('REPORT_COUNTRY'),
      dataIndex: 'countryName',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => a.countryName?.localeCompare(b.countryName),
    },
    {
      width: 150,
      title: getTranslations('REPORT_CITY'),
      dataIndex: 'city',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => a.city?.localeCompare(b.city),
    },
    {
      width: 150,
      title: getTranslations('REPORT_CONTRACT_START_DATE'),
      dataIndex: 'contractDate',
      sortDirections: ["descend", "ascend"],
      render: (contractDate) => {
        return moment(contractDate).format("DD-MM-YYYY");
      },
    },
    {
      width: 120,
      title: getTranslations('REPORT_NOTIFICATION_SMS'),
      dataIndex: ['notificationTypesEnabled', 'sms'],
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => {
        if (a.notificationTypesEnabled.sms === b.notificationTypesEnabled.sms) {
          return 0
        }
        if (!!a.notificationTypesEnabled.sms > !!b.notificationTypesEnabled.sms) {
          return 1
        }
        return -1;
      },
      render: (enabled) => {
        return enabled ? getTranslations('REPORT_YES') : getTranslations('REPORT_NO');
      },
    },
    {
      width: 120,
      title: getTranslations('REPORT_NOTIFICATION_PUSH'),
      dataIndex: ['notificationTypesEnabled', 'web'],
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => {
        if (a.notificationTypesEnabled.web === b.notificationTypesEnabled.web) {
          return 0
        }
        if (!!a.notificationTypesEnabled.web > !!b.notificationTypesEnabled.web) {
          return 1
        }
        return -1;
      },
      render: (enabled) => {
        return enabled ? getTranslations('REPORT_YES') : getTranslations('REPORT_NO');
      },
    },
    {
      width: 120,
      title: getTranslations('REPORT_NOTIFICATION_EMAIL'),
      dataIndex: ['notificationTypesEnabled', 'email'],
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => {
        if (a.notificationTypesEnabled.email === b.notificationTypesEnabled.email) {
          return 0
        }
        if (!!a.notificationTypesEnabled.email > !!b.notificationTypesEnabled.email) {
          return 1
        }
        return -1;
      },
      render: (enabled) => {
        return enabled ? getTranslations('REPORT_YES') : getTranslations('REPORT_NO');
      },
    },
    {
      width: 170,
      title: fulfillmentColumnTitle,
      dataIndex: 'maxActiveStudents',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => b.activeUsersCount - a.activeUsersCount,
      render: (count, entry) => {
        const percentage = (entry.activeUsersCount * 100) / count;
        const color = ((value) => {
          if (value > 90) {
            return '#ec5757'; //red
          }
          if (value > 70 && value <= 90) {
            return '#fbbf21'; //yellow
          }
          return '#44d4a8'; //green
        })(percentage);

        return <Dot value={count} color={color} />
      },
    },
    {
      width: 150,
      title: getTranslations('REPORT_ENABLED_USERS_COUNT'),
      dataIndex: 'activeUsersCount',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => b.activeUsersCount - a.activeUsersCount,
    },
    {
      width: 150,
      title: getTranslations('REPORT_DISABLED_USERS_COUNT'),
      dataIndex: 'inactiveUsersCount',
      sortDirections: ["descend", "ascend"],
      sorter: (a: ICompanyReport, b: ICompanyReport) => ((b.inactiveUsersCount || 0) - (a.inactiveUsersCount || 0)),
    }
  ];

  const handleTableChange = () => { }

  const handleDownload = () => {
    const headers = [...EXCEL_HEADERS()];
    const data: DataType = companiesData.map(company => {
      const { inactiveUsersCount = 0, notificationTypesEnabled: { sms = false, web = false, email = false } = {} } = company;

      return [
        company.name,
        company.status === StatusEnum.Enable ? getTranslations('REPORT_ENABLED') : getTranslations('REPORT_DISABLED'),
        company.companyTypeName ?? '',
        company.countryName,
        company.city,
        moment(company.contractDate).format("DD-MM-YYYY"),
        sms ? 'Si' : 'No',
        web ? 'Si' : 'No',
        email ? 'Si' : 'No',
        company.maxActiveStudents,
        company.activeUsersCount,
        inactiveUsersCount
      ]
    });

    const now = moment().format('HH[h]:mm[m]:ss[s]_DD')
    exportToExcel(
      headers,
      data,
      'Empresa',
      `reporte_empresas_${now}`
    )
  }

  return (
    <div className={styles.mainWrapper}>
      <div className={styles.header}>
        <TileTile text={getTranslations('REPORT_COMPANY_DETAIL')} />
        <Button className={styles.downloadButton} onClick={handleDownload} >
          {getTranslations('REPORT_DOWNLOAD')} <CogIcon size="1.4em" color="#81858b" icon={"Download"} />
        </Button>
      </div>
      <Table<ICompanyReport>
        onChange={handleTableChange}
        locale={undefined}
        loading={dataIsFetching}
        rowKey='_id'
        columns={columns}
        dataSource={companiesData}
        pagination={{ position: ["bottomRight"], pageSize: 10 }}
        scroll={{ y: 372, x: 'max-content' }}
      />
    </div>
  )
}

const fulfillmentColumnTitle = () => {
  return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {getTranslations('REPORT_USERS_COUNT')}&nbsp;<Tooltip
      title={<>
        {getTranslations('REPORT_GREEN')}<br />
        {getTranslations('REPORT_YELLOW')}<br />
        {getTranslations('REPORT_RED')}
      </>}
      className={styles.tooltip}
    >
      <span>
        <CogIcon size="1.4em" color="#81858b" icon={"Attention"} />
      </span>
    </Tooltip>
  </span>;
}
